import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { demoUsers } from '@/mocks/demoData';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const occupancyData = [
  { day: 'Mon', occupancy: 62 },
  { day: 'Tue', occupancy: 58 },
  { day: 'Wed', occupancy: 71 },
  { day: 'Thu', occupancy: 78 },
  { day: 'Fri', occupancy: 85 },
  { day: 'Sat', occupancy: 94 },
  { day: 'Sun', occupancy: 88 },
];

export default function Home() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    setTimeout(() => {
      const success = login(email, password);
      if (success) {
        navigate('/dashboard', { replace: true });
      } else {
        setError('Invalid email or password. Try a demo account below.');
      }
      setLoading(false);
    }, 600);
  };

  const quickLogin = (userEmail: string, userPassword: string) => {
    setError('');
    setLoading(true);
    setEmail(userEmail);
    setPassword(userPassword);

    setTimeout(() => {
      const success = login(userEmail, userPassword);
      if (success) {
        navigate('/dashboard', { replace: true });
      }
      setLoading(false);
    }, 400);
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-50 text-red-700 border-red-200';
      case 'manager': return 'bg-gold-50 text-gold-700 border-gold-200';
      case 'receptionist': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'housekeeping': return 'bg-green-50 text-green-700 border-green-200';
      case 'accountant': return 'bg-purple-50 text-purple-700 border-purple-200';
      default: return 'bg-navy-50 text-navy-600 border-navy-200';
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel — Branding + Chart */}
      <div className="relative flex lg:w-1/2 xl:w-[55%] flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-800 via-navy-700 to-navy-900" />
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "url('https://readdy.ai/api/search-image?query=Luxury%20hotel%20lobby%20interior%20with%20golden%20chandelier%20marble%20floors%20navy%20blue%20velvet%20sofas%20elegant%20reception%20desk%20warm%20ambient%20lighting%20premium%20boutique%20hotel%20aesthetic%20high%20end%20hospitality%20interior%20photography&width=800&height=1000&seq=login-bg-1&orientation=portrait')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-navy-900/55" />

        <div className="relative z-10 flex flex-col h-full p-6 sm:p-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-6 lg:mb-0">
            <div className="w-10 h-10 rounded-xl bg-gold-500 flex items-center justify-center">
              <i className="ri-hotel-line text-navy-900 text-xl" />
            </div>
            <div className="font-display text-2xl font-semibold tracking-wide text-white">
              Hotel<span className="text-gold-400">Pro</span>
            </div>
          </div>

          {/* Middle content */}
          <div className="flex-1 flex flex-col justify-center max-w-lg">
            <blockquote className="text-white/90 text-base sm:text-lg font-light leading-relaxed mb-6">
              "Hospitality is not about grandeur, but about making guests feel genuinely at home."
            </blockquote>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-gold-400">22+</div>
                <div className="text-white/50 text-xs mt-1">Modules</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-gold-400">5</div>
                <div className="text-white/50 text-xs mt-1">Departments</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-gold-400">GST</div>
                <div className="text-white/50 text-xs mt-1">Compliant</div>
              </div>
            </div>

            {/* Occupancy Chart */}
            <div className="hidden lg:block">
              <div className="text-white/60 text-xs font-medium uppercase tracking-wider mb-2">
                Weekly Occupancy Trend
              </div>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={occupancyData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="occGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#c9a84c" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#c9a84c" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
                      domain={[0, 100]}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a2b4a',
                        border: '1px solid rgba(201,168,76,0.3)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#fff',
                      }}
                      formatter={(value: number) => [`${value}%`, 'Occupancy']}
                    />
                    <Area
                      type="monotone"
                      dataKey="occupancy"
                      stroke="#c9a84c"
                      strokeWidth={2}
                      fill="url(#occGrad)"
                      dot={{ r: 3, fill: '#c9a84c', strokeWidth: 0 }}
                      activeDot={{ r: 5, stroke: '#c9a84c', strokeWidth: 2, fill: '#1a2b4a' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Bottom info */}
          <div className="flex items-center gap-3 mt-6 lg:mt-0">
            <div className="w-9 h-9 rounded-full bg-gold-500/20 flex items-center justify-center border border-gold-500/30">
              <i className="ri-hotel-bed-line text-gold-400" />
            </div>
            <div>
              <div className="text-white font-medium text-sm">Complete Hotel Management</div>
              <div className="text-white/50 text-xs mt-0.5">
                Reception &middot; Bookings &middot; Inventory &middot; Canteen
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 lg:px-12 xl:px-16 py-8 sm:py-10 bg-white">
        {/* Mobile-only chart teaser */}
        <div className="lg:hidden w-full max-w-md mb-6">
          <div className="text-navy-500 text-xs font-medium uppercase tracking-wider mb-1.5">
            Weekly Occupancy Trend
          </div>
          <div className="h-28 sm:h-32">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={occupancyData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="occGradM" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#c9a84c" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#c9a84c" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="day"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  domain={[0, 100]}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '12px',
                  }}
                  formatter={(value: number) => [`${value}%`, 'Occupancy']}
                />
                <Area
                  type="monotone"
                  dataKey="occupancy"
                  stroke="#c9a84c"
                  strokeWidth={2}
                  fill="url(#occGradM)"
                  dot={{ r: 3, fill: '#c9a84c', strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="w-full max-w-sm sm:max-w-md">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-2xl font-semibold text-navy-800">Welcome Back</h1>
            <p className="text-navy-400 text-sm mt-1.5">
              Sign in to access your hotel management dashboard
            </p>
          </div>

          {error && (
            <div className="mb-4 sm:mb-5 flex items-start gap-2.5 p-3 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm">
              <i className="ri-error-warning-line text-base mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-300">
                  <i className="ri-mail-line" />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="admin@hotelpro.com"
                  required
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-navy-200 text-sm text-navy-800 placeholder:text-navy-300 focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-300">
                  <i className="ri-lock-line" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Enter your password"
                  required
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-navy-200 text-sm text-navy-800 placeholder:text-navy-300 focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-navy-300 hover:text-navy-500 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-navy-300 text-gold-500 focus:ring-gold-400" />
                <span className="text-navy-500">Remember me</span>
              </label>
              <button type="button" className="text-gold-600 hover:text-gold-700 font-medium">
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-navy-800 text-white text-sm font-semibold hover:bg-navy-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <i className="ri-arrow-right-line" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 sm:mt-8">
            <div className="flex items-center gap-3 mb-4 sm:mb-5">
              <div className="flex-1 h-px bg-navy-100" />
              <span className="text-xs text-navy-400 font-medium uppercase tracking-wider">
                Quick Login
              </span>
              <div className="flex-1 h-px bg-navy-100" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {demoUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => quickLogin(user.email, user.password)}
                  disabled={loading}
                  className="flex flex-row sm:flex-col items-center gap-3 sm:gap-2 p-3 rounded-xl border border-navy-100 hover:border-gold-300 hover:bg-gold-50/50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-navy-800 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">
                    {getUserInitials(user.full_name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-navy-800 truncate">
                      {user.full_name}
                    </div>
                    <div className="text-[11px] text-navy-400 truncate">
                      {user.email}
                    </div>
                    <span className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border uppercase tracking-wide ${getRoleBadge(user.role)}`}>
                      {user.role}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 sm:mt-8 text-center text-xs text-navy-400">
            <p>HotelPro Management System &middot; Demo v1.0</p>
          </div>
        </div>
      </div>
    </div>
  );
}